import { proxyImg } from "@/lib/api";
import { CREATIVE_STYLES } from "@/lib/creativeStyles";

const NAVY = "#0B192C";

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

async function loadImageSafe(src) {
  if (!src) return null;
  try { return await loadImage(proxyImg(src)); } catch { return null; }
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function goldGrad(ctx, x, y, w, h) {
  const g = ctx.createLinearGradient(x, y, x + w, y + h);
  g.addColorStop(0, "#E8C766");
  g.addColorStop(1, "#D4AF37");
  return g;
}

// Cover-fit the photo across the whole frame. Never distorts, never edits pixels.
function drawCover(ctx, img, cw, ch, ox = 0, oy = 0) {
  const iw = img.naturalWidth, ih = img.naturalHeight;
  const s = Math.max(cw / iw, ch / ih);
  const dw = iw * s, dh = ih * s;
  ctx.drawImage(img, ox + (cw - dw) / 2, oy + (ch - dh) / 2, dw, dh);
}

// Cover-fit the photo into a sub-region (used for the panel layout so the whole
// boat stays visible beside a solid panel). Crop/reposition only, boat untouched.
function drawCoverRegion(ctx, img, x, y, w, h) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  const iw = img.naturalWidth, ih = img.naturalHeight;
  const s = Math.max(w / iw, h / ih);
  const dw = iw * s, dh = ih * s;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
}

// Dealership logo on a brand-safe white panel so transparent logos keep contrast.
function drawLogoBadge(ctx, logo, box) {
  if (!logo) return 0;
  const iw = logo.naturalWidth || logo.width;
  const ih = logo.naturalHeight || logo.height;
  if (!iw || !ih) return 0;
  const scale = Math.min(box.maxW / iw, box.maxH / ih);
  const lw = iw * scale, lh = ih * scale;
  const pad = box.pad;
  const panelW = lw + pad * 2, panelH = lh + pad * 2;
  let x;
  if (box.align === "center") x = (box.W - panelW) / 2;
  else if (box.align === "right") x = box.W - panelW - box.margin;
  else x = box.margin;
  const y = box.top;
  ctx.save();
  ctx.fillStyle = "rgba(255,255,255,0.97)";
  roundRect(ctx, x, y, panelW, panelH, 18);
  ctx.fill();
  ctx.drawImage(logo, x + pad, y + pad, lw, lh);
  ctx.restore();
  return panelH;
}

export function shortenSupport(text, max = 30) {
  let s = (text || "").trim();
  if (!s) return "";
  if (s.length <= max) return s;
  if (s.includes(",")) {
    const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
    const last = parts[parts.length - 1];
    if (last && last.length <= max) return last;
    s = last || s;
  }
  if (s.length > max) {
    const cut = s.slice(0, max);
    s = cut.replace(/\s+\S*$/, "").trim() || cut.trim();
  }
  return s;
}

function wrap(ctx, text, maxW, maxLines) {
  const words = (text || "").split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) { lines.push(line); line = w; }
    else line = t;
  }
  if (line) lines.push(line);
  return lines.slice(0, maxLines);
}

// Small gold pill with navy label. Returns its width.
function goldPill(ctx, text, x, y, opts = {}) {
  const { size = 26, h = 52, padX = 20, radius } = opts;
  const t = String(text).toUpperCase();
  ctx.font = `800 ${size}px "Plus Jakarta Sans", sans-serif`;
  const w = ctx.measureText(t).width + padX * 2;
  const r = radius == null ? h / 2 : radius;
  ctx.fillStyle = goldGrad(ctx, x, y, w, h);
  roundRect(ctx, x, y, w, h, r);
  ctx.fill();
  ctx.fillStyle = NAVY;
  ctx.textAlign = "left";
  ctx.fillText(t, x + padX, y + h / 2 + size * 0.34);
  return w;
}

// Spec chips. Wraps across lines within maxW. Returns total height drawn.
function drawChips(ctx, chips, x, y, opts = {}) {
  const { accent = "#D4AF37", filled = false, maxW = 900, gapX = 12, gapY = 12, size = 22, h = 44 } = opts;
  if (!chips || !chips.length) return 0;
  const padX = 18;
  let cx = x, cy = y;
  for (const raw of chips) {
    ctx.font = `800 ${size}px "Plus Jakarta Sans", sans-serif`;
    const t = String(raw).toUpperCase();
    const w = ctx.measureText(t).width + padX * 2;
    if (cx > x && (cx - x + w) > maxW) { cx = x; cy += h + gapY; }
    if (filled) {
      ctx.fillStyle = goldGrad(ctx, cx, cy, w, h);
      roundRect(ctx, cx, cy, w, h, h / 2); ctx.fill();
      ctx.fillStyle = NAVY;
    } else {
      ctx.fillStyle = "rgba(11,25,44,0.5)";
      roundRect(ctx, cx, cy, w, h, h / 2); ctx.fill();
      ctx.lineWidth = 2; ctx.strokeStyle = accent;
      roundRect(ctx, cx, cy, w, h, h / 2); ctx.stroke();
      ctx.fillStyle = "#fff";
    }
    ctx.textAlign = "left";
    ctx.fillText(t, cx + padX, cy + h / 2 + 8);
    cx += w + gapX;
  }
  return (cy - y) + h;
}

// Single centered row of chips (used on the vertical story cover).
function drawChipsCentered(ctx, chips, cx, y, opts = {}) {
  const { accent = "#D4AF37", size = 24, h = 48 } = opts;
  if (!chips || !chips.length) return 0;
  const padX = 20, gap = 14;
  ctx.font = `800 ${size}px "Plus Jakarta Sans", sans-serif`;
  const items = chips.map((t) => {
    const s = String(t).toUpperCase();
    return { s, w: ctx.measureText(s).width + padX * 2 };
  });
  const total = items.reduce((a, it) => a + it.w, 0) + gap * (items.length - 1);
  let x = cx - total / 2;
  for (const it of items) {
    ctx.fillStyle = "rgba(11,25,44,0.5)";
    roundRect(ctx, x, y, it.w, h, h / 2); ctx.fill();
    ctx.lineWidth = 2; ctx.strokeStyle = accent;
    roundRect(ctx, x, y, it.w, h, h / 2); ctx.stroke();
    ctx.fillStyle = "#fff"; ctx.textAlign = "left";
    ctx.fillText(it.s, x + padX, y + h / 2 + 8);
    x += it.w + gap;
  }
  return h;
}

function ctaPill(ctx, text, x, y, opts = {}) {
  const { size = 30, ph = 64, variant = "solid", accent = "#D4AF37" } = opts;
  ctx.font = `800 ${size}px "Plus Jakarta Sans", sans-serif`;
  const padX = variant === "solid-lg" ? 40 : 32;
  const cw = ctx.measureText(text).width + padX * 2;
  ctx.textAlign = "left";
  if (variant === "outline") {
    ctx.fillStyle = "rgba(11,25,44,0.32)";
    roundRect(ctx, x, y, cw, ph, ph / 2); ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = accent;
    roundRect(ctx, x, y, cw, ph, ph / 2); ctx.stroke();
    ctx.fillStyle = accent;
  } else {
    ctx.fillStyle = goldGrad(ctx, x, y, cw, ph);
    if (variant === "solid-lg") { ctx.shadowColor = "rgba(0,0,0,0.35)"; ctx.shadowBlur = 18; ctx.shadowOffsetY = 6; }
    roundRect(ctx, x, y, cw, ph, ph / 2); ctx.fill();
    ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    ctx.fillStyle = NAVY;
  }
  ctx.fillText(text, x + padX, y + ph / 2 + size * 0.34);
  return cw;
}

export function downloadCanvas(canvas, filename) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      resolve();
    }, "image/png");
  });
}

// ---------------------------------------------------------------- layouts (square 1080)

function layoutClean(ctx, img, logo, p) {
  const { W, H, st, subheadline, headline, cta, logoLine, specs } = p;
  drawCover(ctx, img, W, H);
  // soft bottom scrim only where text sits
  const g = ctx.createLinearGradient(0, H * 0.40, 0, H);
  g.addColorStop(0, "rgba(11,25,44,0)");
  g.addColorStop(0.55, "rgba(11,25,44,0.42)");
  g.addColorStop(1, "rgba(11,25,44,0.94)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  // faint top scrim for logo legibility
  const tg = ctx.createLinearGradient(0, 0, 0, 210);
  tg.addColorStop(0, "rgba(11,25,44,0.5)");
  tg.addColorStop(1, "rgba(11,25,44,0)");
  ctx.fillStyle = tg; ctx.fillRect(0, 0, W, 210);

  drawLogoBadge(ctx, logo, { W, top: 52, margin: 56, align: "left", maxW: 210, maxH: 88, pad: 20 });
  if (!logo && logoLine) {
    ctx.textAlign = "left"; ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.font = '700 40px "Fraunces", Georgia, serif';
    ctx.fillText(logoLine, 56, 108);
  }

  const padX = 76, bottom = H - 84;
  const kicker = shortenSupport(subheadline, 34);
  ctx.font = '800 62px "Plus Jakarta Sans", sans-serif';
  const headLines = wrap(ctx, headline || "", W - padX * 2, 3);
  const headLineH = Math.round(62 * 1.16);
  const hasChips = specs && specs.length;
  const ctaH = 64;
  const blockH = 56 + headLines.length * headLineH + (hasChips ? 20 + 44 : 0) + 28 + ctaH;
  let y = bottom - blockH;

  // gold rule + tracked kicker
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.4)"; ctx.shadowBlur = 14; ctx.shadowOffsetY = 2;
  ctx.fillStyle = st.accent; ctx.fillRect(padX, y, 66, 4);
  if (kicker) {
    ctx.font = '800 24px "Plus Jakarta Sans", sans-serif';
    ctx.fillStyle = st.accent; ctx.textAlign = "left";
    ctx.fillText(kicker.toUpperCase(), padX, y + 36);
  }
  y += 56;

  ctx.font = '800 62px "Plus Jakarta Sans", sans-serif';
  ctx.fillStyle = "#fff"; ctx.textAlign = "left";
  for (const l of headLines) { ctx.fillText(l, padX, y + 52); y += headLineH; }
  ctx.restore();

  if (hasChips) { y += 20; drawChips(ctx, specs, padX, y, { accent: st.accent, filled: false, maxW: W - padX * 2 }); y += 44; }

  y += 28;
  if (cta) ctaPill(ctx, cta, padX, y, { ph: ctaH, size: 28, variant: "outline", accent: st.accent });
}

function layoutPanel(ctx, img, logo, p) {
  const { W, H, st, subheadline, headline, cta, logoLine, badge, specs } = p;
  const panelW = Math.round(W * 0.42);
  // whole boat visible in the right region
  drawCoverRegion(ctx, img, panelW, 0, W - panelW, H);
  // solid navy panel
  ctx.fillStyle = NAVY; ctx.fillRect(0, 0, panelW, H);
  // soft feather into the photo for depth
  const eg = ctx.createLinearGradient(panelW, 0, panelW + 150, 0);
  eg.addColorStop(0, "rgba(11,25,44,0.8)");
  eg.addColorStop(1, "rgba(11,25,44,0)");
  ctx.fillStyle = eg; ctx.fillRect(panelW, 0, 150, H);
  // gold seam
  ctx.fillStyle = goldGrad(ctx, panelW - 8, 0, 8, H);
  ctx.fillRect(panelW - 8, 0, 8, H);

  const px = 60, innerW = panelW - px - 40;
  let y = 76;
  const lh = drawLogoBadge(ctx, logo, { W: panelW, top: y, margin: px, align: "left", maxW: innerW - 10, maxH: 92, pad: 18 });
  if (lh) y += lh + 36;
  else {
    if (logoLine) {
      ctx.fillStyle = "#fff"; ctx.font = '700 34px "Fraunces", Georgia, serif'; ctx.textAlign = "left";
      ctx.fillText(logoLine, px, y + 30);
    }
    y += 66;
  }

  if (st.badge && badge) { goldPill(ctx, badge, px, y, { size: 24, h: 50, radius: 8 }); y += 74; }

  const kicker = shortenSupport(subheadline, 26);
  if (kicker) {
    ctx.font = '800 22px "Plus Jakarta Sans", sans-serif';
    ctx.fillStyle = st.accent; ctx.textAlign = "left";
    ctx.fillText(kicker.toUpperCase(), px, y + 22);
    y += 52;
  }

  ctx.font = '800 52px "Plus Jakarta Sans", sans-serif';
  ctx.fillStyle = "#fff"; ctx.textAlign = "left";
  const headLines = wrap(ctx, headline || "", innerW, 5);
  const hLineH = Math.round(52 * 1.12);
  for (const l of headLines) { ctx.fillText(l, px, y + 44); y += hLineH; }

  y += 28;
  if (specs && specs.length) drawChips(ctx, specs, px, y, { accent: st.accent, filled: false, maxW: innerW });

  if (cta) {
    const cy = H - 118;
    ctaPill(ctx, cta, px, cy, { ph: 70, size: 28, variant: "solid", accent: st.accent });
  }
}

function layoutBold(ctx, img, logo, p) {
  const { W, H, st, subheadline, headline, cta, badge, specs } = p;
  drawCover(ctx, img, W, H);
  // strong bottom scrim
  const g = ctx.createLinearGradient(0, H * 0.34, 0, H);
  g.addColorStop(0, "rgba(11,25,44,0)");
  g.addColorStop(0.6, "rgba(11,25,44,0.5)");
  g.addColorStop(1, "rgba(11,25,44,0.97)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  // top scrim
  const tg = ctx.createLinearGradient(0, 0, 0, 250);
  tg.addColorStop(0, "rgba(11,25,44,0.62)");
  tg.addColorStop(1, "rgba(11,25,44,0)");
  ctx.fillStyle = tg; ctx.fillRect(0, 0, W, 250);
  // gold frame
  ctx.lineWidth = 8; ctx.strokeStyle = st.accent;
  roundRect(ctx, 28, 28, W - 56, H - 56, 22); ctx.stroke();

  drawLogoBadge(ctx, logo, { W, top: 52, margin: 56, align: "right", maxW: 216, maxH: 94, pad: 20 });
  if (st.badge && badge) goldPill(ctx, badge, 56, 56, { size: 26, h: 56, radius: 8 });

  const padX = 74, bottom = H - 92;
  const headText = (headline || "").toUpperCase();
  ctx.font = '800 76px "Plus Jakarta Sans", sans-serif';
  const headLines = wrap(ctx, headText, W - padX * 2, 3);
  const hLineH = Math.round(76 * 1.04);
  const kicker = shortenSupport(subheadline, 28);
  const hasChips = specs && specs.length;
  const ctaH = 76;
  const kickH = kicker ? 58 : 0;
  const chipsH = hasChips ? 64 : 0;
  const blockH = kickH + headLines.length * hLineH + chipsH + 30 + ctaH;
  let y = bottom - blockH;

  if (kicker) {
    ctx.font = '800 26px "Plus Jakarta Sans", sans-serif';
    const kw = ctx.measureText(kicker.toUpperCase()).width + 32, kh = 48;
    ctx.fillStyle = st.accent; ctx.fillRect(padX, y, kw, kh);
    ctx.fillStyle = NAVY; ctx.textAlign = "left";
    ctx.fillText(kicker.toUpperCase(), padX + 16, y + kh / 2 + 9);
    y += 58;
  }

  ctx.font = '800 76px "Plus Jakarta Sans", sans-serif';
  ctx.fillStyle = "#fff"; ctx.textAlign = "left";
  ctx.shadowColor = "rgba(0,0,0,0.5)"; ctx.shadowBlur = 18; ctx.shadowOffsetY = 4;
  for (const l of headLines) { ctx.fillText(l, padX, y + 62); y += hLineH; }
  ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;

  if (hasChips) { y += 18; const ch = drawChips(ctx, specs, padX, y, { accent: st.accent, filled: true, maxW: W - padX * 2 }); y += ch; }

  y += 30;
  if (cta) ctaPill(ctx, cta, padX, y, { ph: ctaH, size: 32, variant: "solid-lg", accent: st.accent });
}

export async function renderSquareCreative({ photo, style, subheadline, headline, cta, logoLine, logoImg, badge, specs = [] }) {
  const st = style || CREATIVE_STYLES[0];
  const W = 1080, H = 1080;
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  const img = await loadImage(proxyImg(photo));
  const logo = await loadImageSafe(logoImg);
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = NAVY; ctx.fillRect(0, 0, W, H);

  const p = { W, H, st, subheadline, headline, cta, logoLine, badge, specs };
  if (st.layout === "panel") layoutPanel(ctx, img, logo, p);
  else if (st.layout === "full-bold") layoutBold(ctx, img, logo, p);
  else layoutClean(ctx, img, logo, p);
  return c;
}

// ---------------------------------------------------------------- story cover (9:16)

export async function renderStoryCover({ photo, logoLine, headline, cta, logoImg, style, badge, specs = [] }) {
  const st = style || CREATIVE_STYLES[1];
  const W = 1080, H = 1920;
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  const img = await loadImage(proxyImg(photo));
  const logo = await loadImageSafe(logoImg);
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = NAVY; ctx.fillRect(0, 0, W, H);
  drawCover(ctx, img, W, H);

  const g = ctx.createLinearGradient(0, 0, 0, H);
  if (st.layout === "full-bold") {
    g.addColorStop(0, "rgba(11,25,44,0.78)");
    g.addColorStop(0.45, "rgba(11,25,44,0.12)");
    g.addColorStop(1, "rgba(11,25,44,0.95)");
  } else {
    g.addColorStop(0, "rgba(11,25,44,0.7)");
    g.addColorStop(0.45, "rgba(11,25,44,0.1)");
    g.addColorStop(1, "rgba(11,25,44,0.9)");
  }
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

  if (st.layout === "full-bold") { ctx.lineWidth = 10; ctx.strokeStyle = st.accent; roundRect(ctx, 34, 34, W - 68, H - 68, 28); ctx.stroke(); }

  const badgeH = logo ? drawLogoBadge(ctx, logo, { W, top: 96, align: "center", maxW: 330, maxH: 140, pad: 34 }) : 0;
  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(255,255,255,0.94)";
  ctx.font = '700 50px "Fraunces", Georgia, serif';
  const nameY = logo ? 96 + badgeH + 60 : 170;
  if (logoLine) ctx.fillText(logoLine, W / 2, nameY);

  let topY = nameY + 40;
  if (st.badge && badge) {
    ctx.font = '800 30px "Plus Jakarta Sans", sans-serif';
    const t = badge.toUpperCase();
    const bw = ctx.measureText(t).width + 48, bh = 58, bx = (W - bw) / 2;
    ctx.fillStyle = goldGrad(ctx, bx, topY, bw, bh);
    roundRect(ctx, bx, topY, bw, bh, bh / 2); ctx.fill();
    ctx.fillStyle = NAVY; ctx.textAlign = "center";
    ctx.fillText(t, W / 2, topY + bh / 2 + 10);
  }

  // headline + chips + cta anchored to lower third
  const hCase = st.headlineCase === "upper" ? (headline || "").toUpperCase() : (headline || "");
  const headFont = st.headlineFont === "sans" ? '800 84px "Plus Jakarta Sans", sans-serif' : '800 84px "Fraunces", Georgia, serif';
  ctx.font = headFont;
  const headLines = wrap(ctx, hCase, W - 150, 4);
  const hasChips = specs && specs.length;
  const ctaBlock = cta ? 88 + 30 : 0;
  const chipsBlock = hasChips ? 48 + 28 : 0;
  let y = H - 300 - ctaBlock - chipsBlock - (headLines.length - 1) * 96;

  ctx.fillStyle = "#fff"; ctx.textAlign = "center";
  if (st.layout === "full-bold") { ctx.shadowColor = "rgba(0,0,0,0.45)"; ctx.shadowBlur = 18; ctx.shadowOffsetY = 4; }
  for (const l of headLines) { ctx.fillText(l, W / 2, y); y += 96; }
  ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;

  if (hasChips) { y += 12; const ch = drawChipsCentered(ctx, specs, W / 2, y, { accent: st.accent }); y += ch + 28; }

  if (cta) {
    const ph = 88, padX = 50;
    ctx.font = '800 40px "Plus Jakarta Sans", sans-serif';
    const cw = ctx.measureText(cta).width + padX * 2;
    const x = (W - cw) / 2, cy = y;
    ctx.fillStyle = goldGrad(ctx, x, cy, cw, ph);
    roundRect(ctx, x, cy, cw, ph, ph / 2); ctx.fill();
    ctx.fillStyle = NAVY; ctx.textAlign = "center";
    ctx.fillText(cta, W / 2, cy + ph / 2 + 14);
  }
  return c;
}

// ---------------------------------------------------------------- carousel slide (1080)

export async function renderCarouselSlide({ photo, caption, index }) {
  const W = 1080, H = 1080;
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  const img = await loadImage(proxyImg(photo));
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = NAVY; ctx.fillRect(0, 0, W, H);
  drawCover(ctx, img, W, H);
  const g = ctx.createLinearGradient(0, H * 0.4, 0, H);
  g.addColorStop(0, "rgba(11,25,44,0)");
  g.addColorStop(1, "rgba(11,25,44,0.92)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = "#D4AF37";
  roundRect(ctx, 48, 48, 84, 84, 20); ctx.fill();
  ctx.fillStyle = NAVY;
  ctx.textAlign = "center";
  ctx.font = '800 44px "Plus Jakarta Sans", sans-serif';
  ctx.fillText(String(index), 90, 108);

  ctx.textAlign = "left";
  ctx.fillStyle = "#fff";
  ctx.font = '800 52px "Plus Jakarta Sans", sans-serif';
  const lines = wrap(ctx, caption, W - 128, 3);
  let y = H - 80 - (lines.length - 1) * 62;
  for (const l of lines) { ctx.fillText(l, 64, y); y += 62; }
  return c;
}

export function slugify(s) {
  return (s || "creative").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase().slice(0, 48) || "creative";
}
